import React from 'react';
import { Link } from 'react-router-dom';
import ServiceSubpage, { Lead, SubHead, Included, Callout } from '../components/ServiceSubpage';

export default function ServiceAdvisor() {
  return (
    <ServiceSubpage
      seo={{
        title: 'Fractional AI Advisor & Ongoing AI Consulting for Small Business and Nonprofit | Sagient Partners',
        description: 'Your fractional AI advisor for small businesses and nonprofits — ongoing AI automation maintenance, system oversight, strategy consulting, and monthly reporting. No full-time hire. $2,000–$4,000/month.',
        path: '/services/ai-advisor',
      }}
      hero={{
        tier: 'Tier 3 · AI Advisor · Monthly retainer · $2,000-$4,000/month',
        headline: 'Fractional AI Advisor & Ongoing AI Consulting for Small Business and Nonprofit',
        subhead: 'Your Fractional AI Advisor: strategic guidance, ongoing AI automation maintenance, and system oversight — without full-time headcount. We stay embedded to find new automations, monitor what is live, and report results to leadership every month.',
        meta: 'Senior AI expertise · A fraction of a full-time hire',
        cta: 'Keep the Momentum →',
        ctaHeading: 'Keep the Momentum',
        ctaSub: 'Ongoing AI expertise that keeps your automation footprint current and growing - without a full-time hire.',
      }}
      schema={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            name: 'Fractional AI Advisor for Small Business and Nonprofit',
            serviceType: 'AI Consulting',
            provider: { '@type': 'Organization', name: 'Sagient Partners', url: 'https://sagientpartners.com' },
            description: 'A monthly retainer providing continuous AI strategy, new automation deployment, ongoing AI automation maintenance, system oversight, and executive reporting for small businesses and nonprofits.',
            areaServed: { '@type': 'Country', name: 'United States' },
            audience: [
              { '@type': 'Audience', audienceType: 'Small Business' },
              { '@type': 'Audience', audienceType: 'Nonprofit' },
            ],
            offers: { '@type': 'Offer', priceCurrency: 'USD', priceRange: '$2,000-$4,000/month', lowPrice: '2000', highPrice: '4000', priceSpecification: { '@type': 'UnitPriceSpecification', priceCurrency: 'USD', priceType: 'https://schema.org/SubscriptionPrice' } },
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'Is there a minimum commitment?', acceptedAnswer: { '@type': 'Answer', text: "Yes, in terms of a monthly minimum and duration. We'll cover terms on the Discovery Call. The retainer is built to flex as your needs change." } },
              { '@type': 'Question', name: 'What if I only need occasional help?', acceptedAnswer: { '@type': 'Answer', text: "It scales to the pace of your organization — some months are more focused on new builds, others on optimization, and others on tacit knowledge capture." } },
              { '@type': 'Question', name: 'How much does a fractional AI advisor cost compared to hiring full-time staff?', acceptedAnswer: { '@type': 'Answer', text: 'A full-time AI lead runs six figures. The retainer gives you that same senior expertise for a fraction of the cost — typically $2,000–$4,000/month versus $120,000+ per year for a full-time hire.' } },
            ],
          },
        ],
      }}
      faqs={[
        { q: "Is there a minimum commitment?", a: "Yes, in terms of a monthly minimum and duration. We’ll cover terms on the Discovery Call. The retainer is built to flex as your needs change." },
        { q: "What if I only need occasional help?", a: "It scales to the pace of your organization — some months are more focused on new builds, others on optimization, and others on tacit knowledge capture." },
        { q: "How much does a fractional AI advisor cost compared to hiring full-time staff?", a: "A full-time AI lead runs six figures. The retainer gives you that same senior expertise for a fraction of the cost — typically $2,000–$4,000/month versus $120,000+ per year for a full-time hire." },
      ]}
      prevNext={[{ to: '/services/ai-strategy-implementation', label: '← AI Strategy Implementation' }]}
    >
      <section className="section" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container">
          <p className="section-label">Why Choose a Fractional AI Consultant vs. Hiring Full-Time</p>
          <Lead>
            The implementation of AI isn&rsquo;t always a one-time project. There is always more work that can be
            automated, and as the business evolves, workflows and software change. Most small businesses and nonprofits
            can&rsquo;t justify a full-time AI hire — but they still need ongoing AI automation maintenance and system
            oversight to keep what&rsquo;s running performing and to keep finding new opportunities. A fractional AI
            consultant gives you senior expertise on retainer, at a fraction of the cost of hiring.
          </Lead>

          <SubHead>Ongoing AI Management, Optimization, &amp; Strategy</SubHead>
          <Lead>
            Each month we review your roadmap, identify and deploy new automations, and handle ongoing AI automation
            maintenance and system oversight for everything already running. Just as important, we keep the discipline
            that makes AI work: knowing what you&rsquo;re trying to measure before you change anything. Every workflow
            gets a baseline and a target in terms of hours saved and response time, giving leadership a clear read on
            what it is delivering, and the opportunity to challenge the inherited processes that no longer need to exist.
          </Lead>

          <Callout label="Turn Expertise Into Organizational Memory">
            Beyond automation, we help you capture what lives only in people&rsquo;s heads, from the veterans who
            &ldquo;know how everything works.&rdquo; Called tacit knowledge capture, we document that expertise into AI
            assistants, turning your organization from being person-dependent to knowledge-dependent. Every staff member
            can get the right answer instantly, extending the expert&rsquo;s reach when they&rsquo;re away.
          </Callout>
        </div>
      </section>

      <section className="section section--alt" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 48 }}>
          <div>
            <p className="section-label">What&rsquo;s Included</p>
            <Included items={[
              'Monthly AI roadmap reviews and strategy sessions',
              'New automation identification and deployment each month',
              'Performance monitoring and optimization of live systems',
              'Executive-level reporting',
              'On-call advisory for strategic decisions and vendor management',
            ]} />
          </div>
          <div>
            <p className="section-label">What You Walk Away With</p>
            <Lead>
              An organization that gets measurably more efficient every month without you becoming an in-house AI expert.
            </Lead>
            <p style={{ fontSize: '0.95rem', color: '#0C447C', fontStyle: 'italic', marginTop: 8 }}>
              <strong>Ideal for:</strong> Small businesses and nonprofits looking for ongoing AI automation maintenance,
              system oversight, and tacit knowledge capture at a fraction of the cost of a full-time hire — including
              clients who&rsquo;ve completed a <Link to="/services/ai-readiness-assessment" style={{ color: '#0C447C', fontWeight: 600 }}>Readiness Assessment</Link> or <Link to="/services/ai-strategy-implementation" style={{ color: '#0C447C', fontWeight: 600 }}>Implementation engagement</Link> and want to keep the
              momentum going.
            </p>
          </div>
        </div>
      </section>
    </ServiceSubpage>
  );
}
